"use client";
import Link from "next/link";
import { Clock } from "lucide-react";
import { useMemo } from "react";
import { ICMS } from "@/store/home/homeModel";
import { cleanHeaderTags, createLookupObject, decodeHtml } from "@/lib/helper";
import Banner from "@/components/Banner";
import { OptimizedImage } from "@/components/common/OptimizedImage";

interface ActivitiesClientProps {
  cmsData: ICMS[];
  bannerData: any[];
  activitiesData: any[];
}

export default function Activities({
  cmsData,
  bannerData,
  activitiesData,
}: ActivitiesClientProps) {
  const lookUpCMSData = useMemo(() => {
    return createLookupObject(cmsData || [], "Name");
  }, [cmsData]);

  const banner =
    bannerData?.filter((banner: any) => banner?.BannerType === "activities") ||
    [];

  return (
    <>
      <Banner BannerList={banner} />

      <section className="py-20 bg-gradient-to-b from-white to-[#F3EBDD]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {activitiesData.map((activity) => (
              <Link
                key={activity.ID}
                href={`/activities/${activity.ActivityURL}/`}
                className="group"
              >
                <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all flex flex-col h-full">
                  <div className="relative h-64 overflow-hidden">
                    <OptimizedImage
                      src={activity.MainImage || "#"}
                      alt={activity.ActivityName}
                      width={600}
                      height={400}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3
                      className="text-2xl mb-3 text-[#5A3A1E] group-hover:text-[#BF5500] transition-colors"
                      style={{ fontFamily: "Playfair Display, serif" }}
                    >
                      {activity.ActivityName}
                    </h3>
                    <p
                      className="text-[#5A3A1E]/85 mb-4 line-clamp-3 flex-1"
                      dangerouslySetInnerHTML={{
                        __html: cleanHeaderTags(
                          decodeHtml(activity.ShortDescription),
                        ),
                      }}
                    />
                    <div className="flex items-center gap-4 text-sm text-[#5A3A1E]/85">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {activity.Duration}
                      </span>
                      <span className="flex items-center gap-1">
                        📊 {activity.DifficultyType}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {lookUpCMSData?.["activities - Why Choose Our Experiences"] && (
        <div
          dangerouslySetInnerHTML={{
            __html: cleanHeaderTags(
              decodeHtml(
                lookUpCMSData?.["activities - Why Choose Our Experiences"]
                  ?.Template,
              ),
            ),
          }}
        />
      )}
      {lookUpCMSData?.["activities - CTA"] && (
        <div
          dangerouslySetInnerHTML={{
            __html: cleanHeaderTags(
              decodeHtml(lookUpCMSData?.["activities - CTA"]?.Template),
            ),
          }}
        />
      )}
    </>
  );
}
